import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import { useDisableScrollInModal } from 'hooks/useDisableScrollInModal';
import { useTranslation } from 'hooks/useTranslation';
import { renderMultiKeyStrongText } from 'utils/textHtml/renderMultiKeysStronText';
import { isKeysInApa } from 'utils/apa/isKeysInApa';
import {
  PAYMENT_METHOD_CONFIRM_MODAL_KEYS,
  PAYMENT_METHOD_CONFIRM_MODAL_GATEWAY_KEYS,
} from '../../translationKeys';
import { GATEWAY_CODES } from '../../constants';
import './style.scss';

const DEFAULT_SECONDS = 3;
const ERROR_MAX_LENGTH = 95;

export const PaymentMethodConfirmModal = ({
  show,
  type = 'add',
  status = 'success',
  gateway = GATEWAY_CODES.TDC,
  onFinish,
}) => {
  const assets = useSelector(state => state.asset);
  const translations = useSelector(state => state.translations);
  const modalsConfig = useSelector(
    selectMetadataKeyByRegion('payment_method_modals_config'),
  );
  const { translationMapper } = useTranslation();
  const translatedTexts = useMemo(
    () => translationMapper(PAYMENT_METHOD_CONFIRM_MODAL_KEYS),
    [translationMapper],
  );
  const gatewayTranslatedTexts = useMemo(
    () => translationMapper(PAYMENT_METHOD_CONFIRM_MODAL_GATEWAY_KEYS, gateway),
    [translationMapper, gateway],
  );

  const isError = status === 'error';
  const isUpdate = type === 'update';

  const icon = (isError ? assets?.Icono_Incorrecto : assets?.Icono_Correcto) || '';

  const secondsToShow = isError
    ? modalsConfig?.modal_add_error_seconds
    : isUpdate
      ? modalsConfig?.modal_update_confirmation_seconds
      : modalsConfig?.modal_add_confirmation_seconds;
  const timeToShowModal = (secondsToShow || DEFAULT_SECONDS) * 1000;

  useDisableScrollInModal(show);

  useEffect(() => {
    if (!show) return undefined;

    const timer = setTimeout(() => {
      onFinish?.();
    }, timeToShowModal);

    return () => clearTimeout(timer);
  }, [show, timeToShowModal]);

  if (!show) return null;

  const renderText = () => {
    if (isError) {
      const errorKeys = [
        `${PAYMENT_METHOD_CONFIRM_MODAL_GATEWAY_KEYS.ERROR_TEXT_1.key}_${gateway}`,
        `${PAYMENT_METHOD_CONFIRM_MODAL_GATEWAY_KEYS.ERROR_TEXT_2.key}_${gateway}`,
        `${PAYMENT_METHOD_CONFIRM_MODAL_GATEWAY_KEYS.ERROR_TEXT_3.key}_${gateway}`,
      ];
      const text1 = gatewayTranslatedTexts.ERROR_TEXT_1;
      const text2 = gatewayTranslatedTexts.ERROR_TEXT_2;
      const text3 = gatewayTranslatedTexts.ERROR_TEXT_3;

      return renderMultiKeyStrongText(
        isKeysInApa(errorKeys, translations),
        `${text1} ${text2} ${text3}`,
        ERROR_MAX_LENGTH,
        <p className="payment-method-confirm-modal-text">
          {text1} <strong>{text2}</strong> {text3}
        </p>,
      );
    }

    if (isUpdate) {
      return (
        <p className="payment-method-confirm-modal-text">
          {translatedTexts.UPDATE_TEXT}
        </p>
      );
    }

    return (
      <p className="payment-method-confirm-modal-text">
        <strong>{gatewayTranslatedTexts.VERIFICATION_TEXT_1}</strong>{' '}
        {gatewayTranslatedTexts.VERIFICATION_TEXT_2}
      </p>
    );
  };

  return (
    <div className="payment-method-confirm-modal-container">
      <div className="payment-method-confirm-modal-container-card-container">
        <div className="payment-method-confirm-modal-container-card-subcontainer">
          <img
            className="payment-method-confirm-modal-container-card-icon"
            src={icon}
            alt=""
          />

          {renderText()}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodConfirmModal;
